import { context, trace, propagation } from '@opentelemetry/api';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { noop } from 'lodash';
import { useCurrentValueGetter, useIsHeavyAccount } from 'hooks';
import { DEFAULT_REQUEST_HEADERS } from 'services/api/defaultRequestHeaders';
import { IS_DEBUG_MODE } from '../../../services/isDebugMode';
import { useAccount } from '../account-configurations';
import { APP_EVENT_TYPE, useAppEventContext } from '../app-events-aggregator';
import { useAppEventExtension } from '../app-events-aggregator/hooks/useAppEventExtension';
import { authentificationSelector, userSelector } from '../authentication';
import { throwCriticalError } from '../formData/utils/throwCriticalError';
import { useIsTracerPermitted } from './hooks/useIsTracerPermitted';
import { useOnBrowserUnfreezed } from './hooks/useOnBrowserUnfreezed';
import { useTracerFilterConfig } from './hooks/useTracerFilterConfig';
import { startTracing, TRACER_NAME } from './utils/tracerConfiguration';
import { TRACER_STATUS_CODE } from './utils/tracerStatusCode';
import { TRACER_STATUS_MESSAGE } from './utils/tracerStatusMessage';

export const getSpanAlreadyExistsError = name => new Error(`Span with the same name(${name}) already exists.`);
const EMPTY_MAP = new Map();
const CHILD_EMPTY_MAP = new Map();
(() => {
    if (IS_DEBUG_MODE()) {
        window.ROOT_TRACE_SPAN_MAP = EMPTY_MAP;
        window.CHILD_TRACE_SPAN_MAP = CHILD_EMPTY_MAP;
    }
})();

const DEFAULT_TRACER_CONTEXT_VALUE = {
    createRootSpan: noop,
    getRootSpan: noop,
    endRootSpan: noop,
    startSpan: noop,
    getSpan: noop,
    endSpan: noop,
    setSpanStatus: noop,
    terminateMeasurement: noop,
    isEmptyRootSpan: noop,
    getTraceRequestHeader: noop,
};
export const TracerContext = createContext(DEFAULT_TRACER_CONTEXT_VALUE);

export const useTracerContext = () => useContext(TracerContext);

export const TracerProvider = ({ children }) => {
    const isTracingEnabled = useIsTracerPermitted();
    const { push: pushAppEvent } = useAppEventContext();
    const isAuthenticated = useSelector(authentificationSelector);
    const tracerFilter = useTracerFilterConfig();
    const spanFiltersConfigGetter = useCurrentValueGetter(tracerFilter);

    const { accountName, type: accountType } = useAccount();
    const accountTypeGetter = useCurrentValueGetter(accountType);
    const { user_id } = useSelector(userSelector);
    const extendEvent = useAppEventExtension();
    const isHeavyAccount = useIsHeavyAccount();
    const isHeaveAccountGetter = useCurrentValueGetter(isHeavyAccount);

    const tracer = trace.getTracer(TRACER_NAME);
    const globalSpanRef = useRef(EMPTY_MAP);
    const childSpansRef = useRef(CHILD_EMPTY_MAP);

    const hasBeenFreezedRef = useRef();
    const batchSpanProcessorRef = useRef();

    const setEventAttributes = useCallback(
        async span => {
            const appEventProperties = await extendEvent({ type: APP_EVENT_TYPE.PERFORMANCE_MEASUREMENT });
            span.setAttributes({ appEventProperties });
        },
        [extendEvent]
    );

    const setSpanStatus = useCallback((span, status, message) => {
        if (span) {
            span.setStatus({ code: status, message });
        }
    }, []);
    const createRootSpan = useCallback(
        (name, options) => {
            if (globalSpanRef.current.has(name)) {
                throwCriticalError(getSpanAlreadyExistsError(name));
            }

            const span = tracer.startSpan(name, options);
            globalSpanRef.current.set(name, span);

            span.setAttributes({
                UserId: user_id,
                accountType: accountTypeGetter(),
                isHeavyAccount: isHeaveAccountGetter(),
            });
            setEventAttributes(span).catch(error => {
                console.error('Failed to add events to span:', error);
            });

            return span;
        },
        [tracer, user_id, accountTypeGetter, isHeaveAccountGetter, setEventAttributes]
    );
    const getRootSpan = useCallback(name => globalSpanRef.current.get(name), []);
    const endRootSpan = useCallback(
        name => {
            const rootSpan = globalSpanRef.current.get(name);

            if (!rootSpan) {
                return;
            }

            if (hasBeenFreezedRef.current && hasBeenFreezedRef.current()) {
                setSpanStatus(rootSpan, TRACER_STATUS_CODE.ERROR, TRACER_STATUS_MESSAGE.TERMINATE_TRACE_MESSAGE);
            }

            const rootSpanId = rootSpan.spanContext().spanId;
            childSpansRef.current.forEach((childSpan, childKey) => {
                if (childSpan.parentSpanId === rootSpanId) {
                    childSpan.end();
                    childSpansRef.current.delete(childKey);
                }
            });
            rootSpan.end();
            if (batchSpanProcessorRef.current) {
                batchSpanProcessorRef.current.forceFlush();
            }
            globalSpanRef.current.delete(name);
        },
        [setSpanStatus]
    );
    const isEmptyRootSpan = useCallback(name => {
        const rootSpan = globalSpanRef.current.get(name);
        if (!rootSpan) {
            return false;
        }

        const rootSpanId = rootSpan.spanContext().spanId;
        let spanChildren = [];
        try {
            spanChildren = [...childSpansRef.current.values()];
        } catch {}
        const hasAnyChild = spanChildren.find(childSpan => childSpan.parentSpanId === rootSpanId);
        return !hasAnyChild;
    }, []);

    const startSpan = useCallback(
        (name, parentSpan, attributes = {}) => {
            if (!parentSpan) {
                return;
            }
            const ctxWithParent = trace.setSpan(context.active(), parentSpan);

            context.with(ctxWithParent, () => {
                const childSpan = tracer.startSpan(name);
                childSpan.setAttributes(attributes);

                childSpansRef.current.set(name, childSpan);
            });
        },
        [tracer]
    );

    const getSpan = useCallback(name => childSpansRef.current.get(name), []);

    const endSpan = useCallback(name => {
        const span = childSpansRef.current.get(name);
        if (span) {
            span.end();
            childSpansRef.current.delete(name);
        }
    }, []);

    const getTraceRequestHeader = useCallback(span => {
        if (!span) {
            return DEFAULT_REQUEST_HEADERS;
        }
        const headers = { ...DEFAULT_REQUEST_HEADERS };
        const updatedContext = trace.setSpan(context.active(), span);
        propagation.inject(updatedContext, headers);

        return headers;
    }, []);

    const terminateMeasurement = useCallback(() => {
        globalSpanRef.current?.forEach((span, name) => {
            setSpanStatus(span, TRACER_STATUS_CODE.ERROR, TRACER_STATUS_MESSAGE.TERMINATE_TRACE_MESSAGE);
            endRootSpan(name);
        });
    }, [endRootSpan, setSpanStatus]);

    hasBeenFreezedRef.current = useOnBrowserUnfreezed({ callback: terminateMeasurement });

    useEffect(() => {
        globalSpanRef.current.clear();
        childSpansRef.current.clear();
        if (isAuthenticated) {
            const { batchSpanProcessor } = startTracing(spanFiltersConfigGetter, pushAppEvent);
            batchSpanProcessorRef.current = batchSpanProcessor;
        }
    }, [isAuthenticated, spanFiltersConfigGetter, pushAppEvent]);

    useEffect(terminateMeasurement, [accountName, terminateMeasurement]);

    const value = useMemo(
        () => ({
            createRootSpan,
            getRootSpan,
            endRootSpan,
            startSpan,
            getSpan,
            endSpan,
            setSpanStatus,
            isEmptyRootSpan,
            getTraceRequestHeader,
        }),
        [
            createRootSpan,
            getRootSpan,
            endRootSpan,
            startSpan,
            getSpan,
            endSpan,
            setSpanStatus,
            isEmptyRootSpan,
            getTraceRequestHeader,
        ]
    );

    return (
        <TracerContext.Provider value={isTracingEnabled ? value : DEFAULT_TRACER_CONTEXT_VALUE}>
            {children}
        </TracerContext.Provider>
    );
};
